import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  Renderer2,
  HostListener,
  ChangeDetectorRef,
  AfterViewInit,
  CUSTOM_ELEMENTS_SCHEMA
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { CommonModule } from '@angular/common';
import { DataService, Card, Level1Topic, Level2Topic, Level3Topic, TopicItem, TopicsResponse, TopicTreeResponse, TopicTreeNode } from '../../services/data.service';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { NavigationAppComponent } from './navigation-app/navigation-app.component';
import { MainAreaComponent } from './main-area/main-area.component';
import { DetailsAreaComponent } from './details-area/details-area.component';
import { AppHeaderComponent } from './app-header/app-header.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LayoutSwitcherComponent } from './layout-switcher/layout-switcher.component';
import { SingleTreeComponent } from './single-tree/single-tree.component';
import { FontSizeService, FontSizeOption } from '../../services/font-size.service';
import { finalize, takeUntil, catchError, tap } from 'rxjs/operators';
import { NavigationPathService } from '../../services/navigation-path.service';
import {FooterComponent} from '../footer/footer.component';
import { RoleService } from '../../services/role.service';
import { BlogHomeComponent } from '../blog-home/blog-home.component';

@Component({
  selector: 'app-home',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    MatToolbarModule,
    MatIconModule,
    MatDividerModule,
    MainAreaComponent,
    DetailsAreaComponent,
    AppHeaderComponent,
    MatProgressSpinnerModule,
    LayoutSwitcherComponent,
    SingleTreeComponent,
    FooterComponent,
    BlogHomeComponent
  ],
  templateUrl: './app-home.component.html',
  styleUrls: ['./app-home.component.css']
})
export class AppHomeComponent implements OnInit, OnDestroy, AfterViewInit {
  userName = 'DemoUser';
  subjectChanged = new Subject<string>();

  // Navigation collapse state
  isNavigationCollapsed = false;

  // Preloading state
  isPreloading = true;

  // Pane-1 navigation state
  p1Mode: 'topicwise' | 'yearwise' = 'topicwise';
  selectedSubject: string | null = null;
  selectedSubItem: string | null = null;
  selectedTopic: string | null = null;
  selectedSubTopic: string | null = null;
  selectedYearwiseExam: string | null = null;
  selectedYearwiseLower: string | null = null;
  selectedYear: string | null = null;

  // Data arrays
  p1Subjects: string[] = [];
  p1SubMap: {[key: string]: string[]} = {};
  p2TopicsMap: {[key: string]: string[]} = {};
  p2SubTopicsMap: {[key: string]: string[]} = {};
  p1YearwiseExams: string[] = []; // Clear hardcoded data
  p1YearwiseLower: {[key: string]: string[]} = {}; // Clear hardcoded data
  p2Years: {[key: string]: string[]} = {};

  // Loading states
  isLoadingSubjects = false;
  isLoadingSections = false;
  isLoadingTopics = false;
  isLoadingSubtopics = false;
  isLoadingTopicQuestions = false;

  // Search properties
  searchTerm = '';
  filterMode = false;

  // Currently selected card (for Pane 4 details)
  selectedCard: Card | null = null;

  // Layout properties
  mainAreaFlex = '0 0 60%';
  detailsAreaFlex = '0 0 40%';
  showResetNotification = false;
  readonly NAV_WIDTH = 20; // Width of navigation area (%)

  // Blog mode layout properties
  blogHomeAreaFlex = '0 0 75%';
  blogDetailsAreaFlex = '0 0 25%';

  // New mapping to keep track of which subtopic belongs to which topic
  subtopicToTopicMap: {[subtopic: string]: string} = {};

  // Map to store subject IDs by name for API calls
  subjectIdMap: {[subjectName: string]: string} = {};

  // Map to store level1 topic IDs
  level1TopicIdMap: {[topicName: string]: string} = {};

  // Map to store level2 topic IDs
  level2TopicIdMap: {[topicName: string]: string} = {};

  // Map to store level3 topic IDs
  level3TopicIdMap: {[topicName: string]: string} = {};

  // Add this property to the class
  loadedQuestions: Card[] = [];

  // Add ViewChild reference to the navigation-app component
  @ViewChild('navigationApp') navigationApp!: NavigationAppComponent;

  // Map to store topic IDs by name for API calls - supports any level
  topicIdMap: { [topicName: string]: { id: string, level: number, hasChildren: boolean, hindiName?: string } } = {};

  // Add the missing properties
  isPaneOne_Collapsed = false;
  isPaneTwo_Collapsed = false;

  // Show details toggle property
  showDetails = false; // Default to hiding details pane

  // New property for single tree layout
  showSingleTree = true;

  // Hindi name mapping
  hindiNameMap: {[key: string]: string} = {};

  // Add ViewChild reference to the single-tree component
  @ViewChild('singleTreeComponent') singleTreeComponent!: SingleTreeComponent;

  // Add ViewChild reference to the blog-home component
  @ViewChild('blogHomeComponent') blogHomeComponent!: BlogHomeComponent;

  // Add this property to track whether to show main area when in fullscreen mode
  showMains = true;

  // Mobile view properties
  isMobileView = false;
  showMobileNav = false;
  showMobileDetails = false;

  // Add destroy Subject for subscription management
  private destroy$ = new Subject<void>();

  // Add currentFontSize property
  currentFontSize: FontSizeOption = 'medium';

  // Navigation area flex property
  navigationAreaFlex = '0 0 20%'; // Default width for navigation-area

  // Reference to the MainAreaComponent
  @ViewChild(MainAreaComponent) mainAreaComponent!: MainAreaComponent;

  // Properties for panel resizing
  isDragging = false;
  startX = 0;
  currentMainWidth = 75; // Changed from 80 to 75 for main area (%)
  readonly MIN_MAIN_WIDTH = 20; // Minimum width for main area (%)
  readonly MAX_MAIN_WIDTH = 80; // Maximum width for main area (%)

  // Properties for navigation resizer
  isNavDragging = false;
  navStartX = 0;
  currentNavWidth = 20; // Changed from 10 to 20 for single tree area (%)
  readonly MIN_NAV_WIDTH = 10; // Minimum width for nav area (%)
  readonly MAX_NAV_WIDTH = 40; // Maximum width for nav area (%)

  // Add this property to track whether the user is internal
  isInternalUser = false;

  // Layout and navigation state - add horizontal tabs property
  showHorizontalTabs = false;

  // Blog mode state
  blogMode = false;

  // Change nav view flag (for floating header mode)
  changeNavView = false;

  constructor(
    private dataService: DataService,
    private router: Router,
    private authService: AuthService,
    private renderer: Renderer2,
    private fontSizeService: FontSizeService,
    private cdr: ChangeDetectorRef,
    private navigationPathService: NavigationPathService,
    private roleService: RoleService
  ) {
    console.log('AppHomeComponent constructor called');
  }

  ngOnInit(): void {
    console.log('AppHomeComponent: ngOnInit() called');

    // Always start in single tree mode
    this.showSingleTree = true;

    // Set proper layout proportions: 20% for tree, 75% for main, 25% for details
    this.mainAreaFlex = '0 0 75%';
    this.detailsAreaFlex = '0 0 25%';
    this.navigationAreaFlex = '0 0 20%';
    this.currentNavWidth = 20;

    // Get user information from localStorage if available
    const storedUser = localStorage.getItem('googleUser');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      if (user.name.length >= 15) {
        this.userName = user.name.split(' ')[0] || 'User';
      } else {
        this.userName = user.name || 'User';
      }
    } else {
      this.userName = 'User'; // Default if no user info is found
    }

    // Check if user is internal - Initialize from localStorage first
    const userRole = localStorage.getItem('userRole');
    this.isInternalUser = userRole === 'internal';

    // Details pane always starts hidden — user opens it by clicking a file
    this.showDetails = false;

    console.log('Initial user role from localStorage:', userRole, 'isInternalUser:', this.isInternalUser, 'showDetails:', this.showDetails);

    // Make sure RoleService is initialized with localStorage value
    if (userRole) {
      this.roleService.setRole(userRole);
    }

    // Subscribe to role changes
    this.roleService.currentRole$.subscribe(role => {
      this.isInternalUser = role === 'internal';
      console.log('Role changed, isInternalUser:', this.isInternalUser);

      // Do not auto-show details pane on role change

      this.updateChildComponentsWithUserRole();
    });

    // Subscribe to auth state changes
    this.authService.authState$.subscribe(isLoggedIn => {
      if (isLoggedIn) {
        // When logged in, refresh user role
        this.checkUserRole();
      } else {
        // When logged out, ensure internal flag is off
        this.isInternalUser = false;
        this.showDetails = false;
        this.updateChildComponentsWithUserRole();
      }
    });

    // Initialize default panel sizes
    this.loadPanelSizes();

    // Check if in mobile mode based on screen size
    this.checkIfMobileView();

    // Initialize data based on the selected mode
    if (this.p1Mode === 'yearwise') {
      // If in yearwise mode, initiate the yearwise data flow
      if (this.p1YearwiseExams.length > 0) {
        this.onYearwiseExamSelect(this.p1YearwiseExams[0]);
      }
    }

    // Show loading indicator
    this.isPreloading = true;
    document.title = 'MindMapsForUPSC';

    // Load subjects from API
    this.loadSubjectsFromApi();

    // Load Hindi names for the single tree
    this.loadHindiNamesForUI();

    // Set a timeout to ensure tree is expanded after initial data load
    setTimeout(() => {
      this.expandTreeAfterLoad();
    }, 1500);
  }

  // Check and update the user role
  private checkUserRole(): void {
    const userRole = localStorage.getItem('userRole');
    this.isInternalUser = userRole === 'internal';

    // Details pane stays hidden — user opens it by clicking a file

    console.log('User role checked:', userRole, 'isInternalUser:', this.isInternalUser, 'showDetails:', this.showDetails);

    // Force change detection to ensure UI updates
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 100);
  }

  // Update child components with the current user role
  private updateChildComponentsWithUserRole(): void {
    console.log('Updating child components with user role, isInternalUser:', this.isInternalUser);

    // Update SingleTreeComponent
    if (this.singleTreeComponent) {
      this.singleTreeComponent.isInternalUser = this.isInternalUser;
      // Force change detection to ensure the UI updates
      this.cdr.detectChanges();
    }

    // Update MainAreaComponent
    if (this.mainAreaComponent) {
      this.mainAreaComponent.isInternalUser = this.isInternalUser;
      // Force change detection to ensure the UI updates
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    // Remove resize listener
    window.removeEventListener('resize', this.onWindowResize.bind(this));

    this.destroy$.next();
    this.destroy$.complete();

    // Unregister event listeners to prevent memory leaks
    this.unregisterEventListeners();
  }

  // Load saved panel sizes from local storage
  loadPanelSizes() {
    if (typeof(Storage) !== "undefined") {
      try {
        // Load main area and details area flex values
        const savedMainAreaFlex = localStorage.getItem('appHome_mainAreaFlex');
        const savedDetailsAreaFlex = localStorage.getItem('appHome_detailsAreaFlex');
        const savedShowDetails = localStorage.getItem('appHome_showDetails');

        if (savedMainAreaFlex) {
          this.mainAreaFlex = savedMainAreaFlex;
        }
        if (savedDetailsAreaFlex) {
          this.detailsAreaFlex = savedDetailsAreaFlex;
        }
        if (savedShowDetails) {
          this.showDetails = savedShowDetails === 'true';
        }

        // Load blog mode panel sizes
        const savedBlogHomeAreaFlex = localStorage.getItem('appHome_blogHomeAreaFlex');
        const savedBlogDetailsAreaFlex = localStorage.getItem('appHome_blogDetailsAreaFlex');

        if (savedBlogHomeAreaFlex) {
          this.blogHomeAreaFlex = savedBlogHomeAreaFlex;
        }
        if (savedBlogDetailsAreaFlex) {
          this.blogDetailsAreaFlex = savedBlogDetailsAreaFlex;
        }

        console.log('Panel sizes loaded:', {
          mainAreaFlex: this.mainAreaFlex,
          detailsAreaFlex: this.detailsAreaFlex,
          showDetails: this.showDetails,
          blogHomeAreaFlex: this.blogHomeAreaFlex,
          blogDetailsAreaFlex: this.blogDetailsAreaFlex
        });
      } catch (error) {
        console.error('Error loading panel sizes:', error);
      }
    }
  }

  // Save panel sizes to localStorage
  private savePanelSizes(): void {
    if (typeof(Storage) !== "undefined") {
      try {
        // Save main area and details area flex values
        localStorage.setItem('appHome_mainAreaFlex', this.mainAreaFlex);
        localStorage.setItem('appHome_detailsAreaFlex', this.detailsAreaFlex);
        localStorage.setItem('appHome_showDetails', this.showDetails.toString());
        console.log('Panel sizes saved:', {
          mainAreaFlex: this.mainAreaFlex,
          detailsAreaFlex: this.detailsAreaFlex,
          showDetails: this.showDetails
        });
      } catch (error) {
        console.error('Error saving panel sizes:', error);
      }
    }
  }

  /**
   * Save blog mode panel sizes to localStorage
   */
  private saveBlogPanelSizes(): void {
    if (typeof(Storage) !== "undefined") {
      try {
        // Save blog mode flex values
        localStorage.setItem('appHome_blogHomeAreaFlex', this.blogHomeAreaFlex);
        localStorage.setItem('appHome_blogDetailsAreaFlex', this.blogDetailsAreaFlex);
        console.log('Blog panel sizes saved:', {
          blogHomeAreaFlex: this.blogHomeAreaFlex,
          blogDetailsAreaFlex: this.blogDetailsAreaFlex
        });
      } catch (error) {
        console.error('Error saving blog panel sizes:', error);
      }
    }
  }

  // Reset panel sizes to default
  resetPanelSizes(): void {
    console.log('Resetting panel sizes to default');
    
    // Reset to default flex values
    this.mainAreaFlex = '0 0 75%';
    this.detailsAreaFlex = '0 0 25%';
    this.showDetails = true;
    this.currentMainWidth = 75;

    // Reset blog mode panel sizes to default
    this.blogHomeAreaFlex = '0 0 75%';
    this.blogDetailsAreaFlex = '0 0 25%';

    // Save the reset values
    this.savePanelSizes();
    this.saveBlogPanelSizes();

    // Show notification
    this.showResetNotification = true;
    setTimeout(() => {
      this.showResetNotification = false;
      this.cdr.detectChanges();
    }, 2000);
  }

  // Reset navigation panel to default size
  resetNavPanelSize(): void {
    const defaultNavWidth = 20; // 20% as the default single-tree width

    // Get single-tree component and update its width
    const singleTree = document.querySelector('app-single-tree');
    if (singleTree) {

      (singleTree as HTMLElement).style.flex = `0 0 ${defaultNavWidth}%`;
      (singleTree as HTMLElement).style.minWidth = `${defaultNavWidth}%`;
      this.currentNavWidth = defaultNavWidth;
    }

    // Show reset notification
    this.showResetNotification = true;
    setTimeout(() => {
      this.showResetNotification = false;
    }, 2000);

    // Force change detection
    this.cdr.detectChanges();

    // Save the new settings
    this.saveNavPanelSize();
  }

  // Save navigation panel size to local storage
  saveNavPanelSize(): void {
    try {
      // Get current width from the DOM
      const singleTree = document.querySelector('app-single-tree');
      if (singleTree) {
        const styles = window.getComputedStyle(singleTree);
        const width = parseFloat(styles.width) / window.innerWidth * 100;
        if (!isNaN(width)) {
          // Update the current width for future reference
          this.currentNavWidth = width;
          // Save to local storage
          localStorage.setItem('singleTreeWidth', width.toString());
          console.log('Saved single-tree width:', width);
        }
      }
    } catch (e) {
      console.error('Error saving nav panel size:', e);
    }
  }

  // Handle years list generated from NavigationAppComponent
  onYearsListGenerated(years: string[]): void {
    // If we have a selected paper type, store the years under that key
    if (this.selectedYearwiseLower) {
      this.p2Years[this.selectedYearwiseLower] = years;
    }

    // If we're in yearwise mode and have selected exam and paper but no year,
    // automatically select the first year
    if (this.p1Mode === 'yearwise' &&
        this.selectedYearwiseExam &&
        this.selectedYearwiseLower &&
        !this.selectedYear &&
        years.length > 0) {
      this.onYearSelect(years[0]);
    }
  }

  onYearwiseLowerSelect(lower: string): void {
    this.selectedYearwiseLower = lower;
    // Reset dependent selections
    this.selectedYear = null;

    // If years are available for the selected type, select the first one by default
    const yearsForType = this.p2Years[lower] || [];
    if (yearsForType.length > 0) {
      this.onYearSelect(yearsForType[0]);
    } else {
      // If years aren't loaded yet, wait and try again
      const checkInterval = setInterval(() => {
        const updatedYearsForType = this.p2Years[lower] || [];
        if (updatedYearsForType.length > 0) {
          clearInterval(checkInterval);
          this.onYearSelect(updatedYearsForType[0]);
        }
      }, 300);

      // Clear the interval after 3 seconds in case data never loads
      setTimeout(() => clearInterval(checkInterval), 3000);
    }
  }

  // Handle data loaded from NavigationAppComponent
  onSubjectsLoaded(subjects: string[]): void {
    this.p1Subjects = subjects;
  }

  onSectionsLoaded(data: {subject: string, sections: string[]}): void {
    console.log(`onSectionsLoaded called for subject: ${data.subject} with ${data.sections.length} sections:`, data.sections);
    this.p1SubMap[data.subject] = data.sections;

    // Forward to navigation app if available
    setTimeout(() => {
      if (this.navigationApp) {
        this.navigationApp.onSectionsLoadedFromParent(data);
      }
    }, 0);

    // Auto-selection removed as per requirements
  }

  onTopicsLoaded(data: {section: string, topics: string[]}): void {
 //   console.log(`onTopicsLoaded called for section: ${data.section} with ${data.topics.length} topics:`, data.topics);
    this.p2TopicsMap[data.section] = data.topics;

    // Forward to navigation app if available
    setTimeout(() => {
      if (this.navigationApp) {
        this.navigationApp.onTopicsLoadedFromParent(data);
      }
    }, 0);

    // Auto-selection removed as per requirements
  }

  onSubtopicsLoaded(data: {topic: string, subtopics: string[]}): void {
   // console.log(`onSubtopicsLoaded called for topic: ${data.topic} with ${data.subtopics.length} subtopics:`, data.subtopics);
    this.p2SubTopicsMap[data.topic] = data.subtopics;

    // Forward to navigation app if available
    setTimeout(() => {
      if (this.navigationApp) {
        this.navigationApp.onSubtopicsLoadedFromParent(data);
      }
    }, 0);

    // Update the subtopic-to-topic map for proper parent-child relationships
    data.subtopics.forEach(subtopic => {
      this.subtopicToTopicMap[subtopic] = data.topic;
    });

    // Auto-selection removed as per requirements
  }

  // Handle navigation events from NavigationAppComponent and SingleTreeComponent
  onP1ModeChange(mode: 'topicwise' | 'yearwise' | string): void {
    // Validate the mode to ensure it's either 'topicwise' or 'yearwise'
    if (mode !== 'topicwise' && mode !== 'yearwise') {
      console.error(`Invalid mode: ${mode}. Must be 'topicwise' or 'yearwise'`);
      return;
    }

    // Cast to the correct type
    const typedMode = mode as 'topicwise' | 'yearwise';
    this.p1Mode = typedMode;

    // Reset all selections
    this.selectedSubject = null;
    this.selectedSubItem = null;
    this.selectedTopic = null;
    this.selectedSubTopic = null;
    this.selectedYearwiseExam = null;
    this.selectedYearwiseLower = null;
    this.selectedYear = null;

    // Auto-selection removed as per requirements
  }

  onSubjectSelect(subject: string): void {
    //console.log(`Subject selected: ${subject}`);

    // If this is the same subject, no need to do anything
    if (this.selectedSubject === subject) {
      return;
    }

    this.selectedSubject = subject;

    // Reset all child selections
    this.selectedSubItem = null;
    this.selectedTopic = null;
    this.selectedSubTopic = null;

    // Clear loaded questions
    this.loadedQuestions = [];

    // For bundle filtering, we just need the subject selection
    // No need to load topic trees unless specifically requested
    //console.log(`Subject selected: ${subject} - Ready for section selection`);

    // Skip auto-selecting first section and loading topic trees
    // This will be handled when user explicitly selects a section
  }

  onSubItemSelect(subItem: string): void {
    console.log(`Section selected: ${subItem}`);
    console.log(`App-home: Blog mode is: ${this.blogMode}`);

    // If this is a different section than previously selected, clear child selections
    if (this.selectedSubItem !== subItem) {
      console.log(`Changing selected section from ${this.selectedSubItem} to ${subItem}`);

      // Find the parent subject for this section
      let parentSubject = null;
      for (const subject of this.p1Subjects) {
        const sections = this.p1SubMap[subject] || [];
        if (sections.includes(subItem)) {
          parentSubject = subject;
          break;
        }
      }

      // If parent subject found, update it
      if (parentSubject && this.selectedSubject !== parentSubject) {
        console.log(`Updating parent subject from ${this.selectedSubject} to ${parentSubject}`);
        this.selectedSubject = parentSubject;
      }

      this.selectedSubItem = subItem;

      // Reset topic and subtopic selections when changing sections
      this.selectedTopic = null;
      this.selectedSubTopic = null;

      // Clear loaded questions
      this.loadedQuestions = [];

      // If blog mode is active, force change detection to ensure blog-home component gets the update
      if (this.blogMode) {
        console.log(`App-home: Blog mode is active, triggering change detection for section: ${subItem}`);
        console.log(`App-home: Subject: ${this.selectedSubject}, Section: ${this.selectedSubItem}`);
        this.cdr.detectChanges();
      }
    } else {
      // If same section is re-selected, no need to do anything
      return;
    }

    // We now have both subject and section selected, which is enough for filtering bundles
    // No need to make additional API calls to load topics unless the user specifically navigates to them

    // Only load topics if needed for navigation, not for bundle filtering
    console.log(`Subject=${this.selectedSubject}, Section=${this.selectedSubItem} - Ready for bundle filtering`);

    // Refresh the navigation tree to ensure UI is updated
    setTimeout(() => {
      this.refreshNavigationTree();
    }, 100);
  }

    // Force refresh the navigation tree
  refreshNavigationTree(): void {
    // console.log('Forcing navigation tree refresh');

    // First check if SingleTreeComponent is available
    if (this.singleTreeComponent) {
      // console.log('Using SingleTreeComponent for tree refresh');

      // Update the tree data directly in the component
      this.singleTreeComponent.updateTreeData();

      // After a short delay, try to expand the nodes
      setTimeout(() => {
        this.singleTreeComponent.expandAllNodes();

        // Force UI update
        this.cdr.detectChanges();
      }, 200);
    }

    // Also update the navigation app component if available
    if (this.navigationApp) {
      // console.log('Updating navigation app tree data');

      // First update the tree data
      setTimeout(() => {
        try {
          this.navigationApp.updateTopicTreeData();
          // console.log('Navigation tree data updated');

          // Then update the display
          setTimeout(() => {
            // Force UI refresh
            window.dispatchEvent(new Event('resize'));
            this.cdr.detectChanges();
          }, 200);
        } catch (error) {
          console.error('Error updating navigation tree:', error);
        }
      }, 200); // Small delay to ensure data is ready
    } else {
      console.warn('Navigation app component not available for tree refresh');
    }

    // Force a window resize event to help update the UI
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
      this.cdr.detectChanges();
    }, 300);
  }

  // Add a method to check if a topic has children by making an API call
  checkTopicForChildren(topicName: string, topicId: string, level: number): void {
    console.log(`Checking if topic ${topicName} has children...`);

    // Set loading indicator based on topic level
    if (level <= 1) {
      this.isLoadingTopics = true;
    } else {
      this.isLoadingSubtopics = true;
    }

    this.dataService.getTopicsByLevel(topicId, level)
      .pipe(
        finalize(() => {
          // Reset loading indicators
          this.isLoadingTopics = false;
          this.isLoadingSubtopics = false;
        })
      )
      .subscribe({
        next: (response) => {
          console.log(`API response for ${topicName}:`, response);
          // Update the hasChildren flag for this topic based on the API response
          // Use both the API's has_children flag and check if there are actual children in the response
          const hasChildren = response.has_children || response.children.length > 0;

          console.log(`API response for ${topicName}: has_children=${response.has_children}, actual children=${response.children.length}`);

          // Update the topic metadata
          if (this.topicIdMap[topicName]) {
            this.topicIdMap[topicName].hasChildren = hasChildren;
          } else {
            this.topicIdMap[topicName] = {
              id: topicId,
              level: level,
              hasChildren: hasChildren
            };
          }

          console.log(`Updated hasChildren for ${topicName}: ${hasChildren} (children count: ${response.children.length})`);

          // Force refresh the navigation tree with the updated metadata
              this.refreshNavigationTree();

          // If there are children, load them
          if (hasChildren && response.children.length > 0) {
            // Store the children in the appropriate maps
            const childNames = response.children.map(child => child.name_eng);

            // Also store child topic metadata
            response.children.forEach(child => {
              // Determine if this child should have children based on level
              let childHasChildren = false;

              if (child.item_level === 1) {
                // Topics at level 1 likely have subtopics
                childHasChildren = true;
              } else if (child.item_level === 2) {
                // Subtopics at level 2 might have children but need explicit check later
                childHasChildren = false;
              } else {
                // For deeper levels, we don't assume children by default
                childHasChildren = false;
              }

              // Store the child's metadata
              this.topicIdMap[child.name_eng] = {
                id: child.topic_id,
                level: child.item_level,
                hasChildren: childHasChildren
              };
            });

            if (level >= 2) {
              // For topics at level 2 or higher, store as subtopics
              this.p2SubTopicsMap[topicName] = childNames;

              // Update subtopics in the navigation app
              this.onSubtopicsLoaded({topic: topicName, subtopics: childNames});
            } else if (level === 1) {
              // For level 1 topics, store as topics
              this.p2TopicsMap[topicName] = childNames;

              // Update topics in the navigation app
              this.onTopicsLoaded({section: topicName, topics: childNames});
            }

            // Force another refresh after storing children
            this.refreshNavigationTree();
          } else if (hasChildren && response.children.length === 0) {
            // If has_children is true but no children were returned,
            // we might need to make another call to get children
            console.log(`Topic ${topicName} has children flag but no children in response, may need explicit load`);
      } else {
            // No children, load questions
            this.loadQuestionsForTopic(topicName, topicId);
          }
        },
        error: (error) => {
          console.error(`Error checking for children of ${topicName}:`, error);
        }
      });
  }

  onTopicSelect(topic: string): void {
    console.log(`App-home: Topic selected: ${topic}`);
    console.log(`App-home: Previous selectedTopic: ${this.selectedTopic}`);
    console.log(`App-home: Blog mode is: ${this.blogMode}`);

    // If this is a different topic than previously selected, clear child selections
    if (this.selectedTopic !== topic) {
      console.log(`App-home: Changing selected topic from ${this.selectedTopic} to ${topic}`);
      this.selectedTopic = topic;
      console.log(`App-home: Updated selectedTopic to: ${this.selectedTopic}`);

      // Reset subtopic selection when changing topics
      this.selectedSubTopic = null;

      // Clear loaded questions
      this.loadedQuestions = [];
      
      // If blog mode is active, force change detection to ensure blog-home component gets the update
      if (this.blogMode) {
        console.log(`App-home: Blog mode is active, triggering change detection for topic: ${topic}`);
        this.cdr.detectChanges();
        
        // Verify that the blog-home component received the update
        setTimeout(() => {
          if (this.blogHomeComponent) {
            console.log(`App-home: Blog-home component selectedTopicFromTree is now: ${this.blogHomeComponent.selectedTopicFromTree}`);
            console.log(`App-home: Expected topic: ${topic}`);
          } else {
            console.warn(`App-home: Blog-home component not available to verify topic update`);
          }
        }, 100);
      }

      // Make sure navigation tree is refreshed to reflect selection
      setTimeout(() => {
        this.refreshNavigationTree();
      }, 100);
    }

    // If subtopics for this topic are already loaded in our maps
    if (this.p2SubTopicsMap[topic] && this.p2SubTopicsMap[topic].length > 0) {
      console.log(`Using pre-loaded subtopics for topic: ${topic}`, this.p2SubTopicsMap[topic]);

      // Select the first subtopic since we've cleared the selection above
      if (this.p2SubTopicsMap[topic].length > 0) {
        const firstSubtopic = this.p2SubTopicsMap[topic][0];
        console.log(`Auto-selecting first subtopic: ${firstSubtopic}`);
        this.onSubTopicSelect(firstSubtopic);
      } else {
        console.log(`No subtopics found for topic: ${topic}`);
      }
    } else {
      // Instead of making an API call, see if the topic has children in the topicIdMap
      const topicInfo = this.topicIdMap[topic];
      if (topicInfo) {
        if (topicInfo.hasChildren) {
          console.log(`Topic ${topic} should have children according to topicIdMap, but they're not loaded properly.`);
          // This shouldn't happen if the tree was loaded properly, but we'll handle it gracefully

          // Try to use data from the parent subject's tree instead of making a new API call
          const subjectId = this.getSubjectIdForTopic(topic);
          if (subjectId) {
            console.log(`Using parent subject's tree data for topic: ${topic}`);
            // Do not call loadLevel3Topics, as it would make a new API call
            // Instead, just handle the current topic directly
            this.handleTopicWithNoLoadedSubtopics(topic);
          } else {
            // Only as a last resort, call the legacy method
            console.log(`No parent subject found for topic: ${topic}, using loadTopicTree as replacement`);
            this.loadQuestionsForTopic(topic, topicInfo.id);
          }
        } else {
          // Topic doesn't have children, load questions directly
          console.log(`Topic ${topic} doesn't have children according to topicIdMap, loading questions directly`);
          this.loadQuestionsForTopic(topic, topicInfo.id);

          // Make sure navigation tree is refreshed to reflect selection for a topic without subtopics
          setTimeout(() => {
            this.refreshNavigationTree();
          }, 100);
        }
      } else {
        // We don't have info about this topic, which shouldn't happen
        // But as a fallback, use the replacement method
        console.warn(`No topic info found in topicIdMap for ${topic}, using loadTopicTree as fallback`);
        // Get the subject ID to load the topic tree
        const subjectId = this.getSubjectIdForTopic(topic);
        if (subjectId) {
          this.loadTopicTree(this.selectedSubject || 'Unknown Subject', subjectId);
        } else {
          console.error(`Cannot load topic tree for unknown topic: ${topic}`);
        }
      }
    }
  }

  // Helper method to find the subject ID for a topic by traversing up the hierarchy
  private getSubjectIdForTopic(topicName: string): string {
    // Try to determine which subject this topic belongs to
    for (const subjectName in this.p1SubMap) {
      const sections = this.p1SubMap[subjectName];
      for (const section of sections) {
        if (this.p2TopicsMap[section] && this.p2TopicsMap[section].includes(topicName)) {
          // Found the section and subject this topic belongs to
          return this.getSubjectId(subjectName);
        }
      }
    }
    return '';
  }

  // Handle a topic with no loaded subtopics without making API calls
  private handleTopicWithNoLoadedSubtopics(topicName: string): void {
    console.log(`Handling topic with no loaded subtopics: ${topicName}`);

    // Since we don't have subtopics for this topic but should,
    // we'll just load questions for this topic directly
    const topicInfo = this.topicIdMap[topicName];
    if (topicInfo) {
      console.log(`Loading questions directly for topic: ${topicName}`);
      this.loadQuestionsForTopic(topicName, topicInfo.id);

      // Make sure navigation tree is refreshed to reflect selection for a topic without subtopics
      setTimeout(() => {
        this.refreshNavigationTree();
      }, 100);
    }
  }

  onSubTopicSelect(subtopic: string): void {
    console.log(`Subtopic selected: ${subtopic}`);

    // Update the selected subtopic
    this.selectedSubTopic = subtopic;

    // In mobile view, hide the navigation and show the main area when a subtopic is selected
    if (this.isMobileView) {
      console.log('Mobile view - hiding navigation to show main area');
      this.showMobileNav = false;
      // Ensure we're not showing details initially
      this.showMobileDetails = false;
    }

    // Get the topic ID for this subtopic to load questions
    const topicId = this.topicIdMap[subtopic]?.id;
    if (topicId) {
      console.log(`Loading questions for subtopic: ${subtopic} with ID: ${topicId}`);

      // Don't clear questions immediately - set loading state and only clear
      // when new questions load (or on error)
      this.loadQuestionsForTopic(subtopic, topicId);
    } else {
      console.error(`No topic ID found for subtopic: ${subtopic}`);
      // Only clear questions if we can't load new ones
      this.loadedQuestions = [];
      this.selectedCard = null;
    }
  }

  onYearwiseExamSelect(exam: string): void {
    this.selectedYearwiseExam = exam;
    // Reset dependent selections
    this.selectedYearwiseLower = null;
    this.selectedYear = null;

    // Load papers for the selected exam if needed
    // If papers are available, select the first one by default
    if (this.p1YearwiseLower[exam] && this.p1YearwiseLower[exam].length > 0) {
      this.onYearwiseLowerSelect(this.p1YearwiseLower[exam][0]);
    } else {
      // If papers aren't loaded yet, wait and try again
      const checkInterval = setInterval(() => {
        if (this.p1YearwiseLower[exam] && this.p1YearwiseLower[exam].length > 0) {
          clearInterval(checkInterval);
          this.onYearwiseLowerSelect(this.p1YearwiseLower[exam][0]);
        }
      }, 300);

      // Clear the interval after 3 seconds in case data never loads
      setTimeout(() => clearInterval(checkInterval), 3000);
    }
  }



  onYearSelect(year: string): void {
    this.selectedYear = year;

    // In mobile view, hide the navigation and show the main area when a year is selected
    if (this.isMobileView) {
      console.log('Mobile view - hiding navigation to show main area on year selection');
      this.showMobileNav = false;
      // Ensure we're not showing details initially
      this.showMobileDetails = false;
    }

    // If needed, trigger loading of content for the selected year
    this.loadYearContent(year);
  }

  // Helper function to load content for the selected year
  loadYearContent(year: string): void {
    // This would be replaced with actual API call if needed
    console.log(`Loading content for ${this.selectedYearwiseExam} - ${this.selectedYearwiseLower} - ${year}`);
    // You could add actual data loading logic here if needed
  }

  // Handle selected card changes from main-area
  onSelectedCardChange(card: Card | null): void {
    // Store the previously selected card to determine if this is an initial load or user selection
    const wasCardPreviouslySelected = this.selectedCard !== null;
    this.selectedCard = card;

    // In mobile view, when a card is selected, always show details
    if (this.isMobileView && card) {
      // In mobile view, we need to ensure showDetails is true and then show mobile details
      this.showDetails = true;
      this.toggleMobileDetails(true);

      // Log for debugging
      console.log('Card selected in mobile view, showing details:', card);
    } else {
      // In desktop mode
      // If a card was explicitly selected by user, show the details area
      if (card) {
        this.showDetails = true;
        console.log('Card selected in desktop view, showing details:', card);
      }
    }
  }
// Handle logout
  onLogout(): void {
    this.authService.logout();
    // Reset user name to default
    this.userName = 'User';
    this.router.navigate(['/']);
  }

  // Handle login
  onLogin(): void {
    console.log('User logged in');
    this.authService.notifyLogin();

    // Check user role after login
    setTimeout(() => {
      this.checkUserRole();
      this.updateChildComponentsWithUserRole();
    }, 100);
  }

  // Load subjects from the new API
  loadSubjectsFromApi(): void {
    console.log('Loading subjects from API...');
    this.isLoadingSubjects = true;
    this.isPreloading = true;

    // Use the getSubjectsFromZeroLevel API for consistent data structure
    this.dataService.getSubjectsFromZeroLevel()
      .pipe(
        // Make sure to handle both success and error cases for preloader
        finalize(() => {
          // Always set these to false when API call completes, regardless of success/error
          this.isLoadingSubjects = false;
          this.isPreloading = false;
          console.log('Data loading complete, preloader should be removed');

          // After subjects are loaded and preloading is finished, expand the tree
          setTimeout(() => {
            console.log('Data loading complete, attempting tree expansion');
            this.expandTreeAfterLoad();
          }, 500);
        })
      )
      .subscribe({
        next: (response) => {
          console.log('Subjects API response:', response);
          if (response && response.topics && response.topics.length > 0) {
            // Extract subject names and IDs from the response
            const subjects = response.topics.map(topic => topic.name_eng);

            // Update the p1Subjects array
            this.p1Subjects = subjects;

            // Store IDs in the subjectIdMap for future API calls
            response.topics.forEach(topic => {
              this.subjectIdMap[topic.name_eng] = topic.topic_id;

              // Also update topicIdMap with the subject info
              this.topicIdMap[topic.name_eng] = {
                id: topic.topic_id,
                level: topic.item_level,
                hasChildren: true, // Assume subjects have children until proven otherwise
                hindiName: topic.name_hindi
              };
            });

            // Log the updated maps
            console.log('Updated subjectIdMap:', this.subjectIdMap);
            console.log('Updated subjects in topicIdMap:', this.topicIdMap);

            // Emit the loaded subjects
            this.onSubjectsLoaded(subjects);

            // Use the new method to load all subject trees at once
            this.loadAllSubjectTrees();

            // We'll let the loadAllSubjectTrees method handle auto-selection
          } else {
            console.warn('No subjects found in API response');
            // Ensure preloader is hidden even if no subjects found
            this.isPreloading = false;
          }
        },
        error: (error) => {
          console.error('Error loading subjects from API:', error);
          // Ensure preloader is hidden even on error
          this.isPreloading = false;
        }
      });
  }

  // Load topics for a parent topic at a specific level
  loadTopics(parentName: string, parentId: string, level: number): void {
    console.log(`Loading topics for parent: ${parentName} (ID: ${parentId}) at level: ${level}`);

    // Set appropriate loading flag based on level
    switch (level) {
      case 0:
        this.isLoadingSections = true;
        break;
      case 1:
        this.isLoadingTopics = true;
        break;
      case 2:
        this.isLoadingSubtopics = true;
        break;
      default:
        // For higher levels, we could use the subtopics loading flag
        console.log(`Loading level ${level} topics...`);
        this.isLoadingSubtopics = true;
    }

    // Use the unified API method
    this.dataService.getTopicsByLevel(parentId, level)
      .pipe(
        finalize(() => {
          // Reset loading flag based on level
          switch (level) {
            case 0:
              this.isLoadingSections = false;
              break;
            case 1:
              this.isLoadingTopics = false;
              break;
            case 2:
              this.isLoadingSubtopics = false;
              break;
            default:
    //          console.log(`Finished loading level ${level} topics`);
              this.isLoadingSubtopics = false;
          }
        })
      )
      .subscribe({
        next: (response) => {
     //     console.log(`Received topics for parent: ${parentName}`, response);

          const topicNames = response.children.map(topic => topic.name_eng);
          const currentLevel = response.current_level;
          const nextLevel = response.next_level;

          // Store topic names in the appropriate map based on level or in a new map for deeper levels
          if (currentLevel === 0) {
            // For subjects (level 0)
            this.p1SubMap[parentName] = topicNames;
          } else if (currentLevel === 1) {
            // For sections (level 1)
            this.p2TopicsMap[parentName] = topicNames;
          } else if (currentLevel === 2) {
            // For topics (level 2)
            this.p2SubTopicsMap[parentName] = topicNames;

            // Map subtopics to their parent topic for reference
            response.children.forEach(subtopic => {
              this.subtopicToTopicMap[subtopic.name_eng] = parentName;
            });
          } else {
            // For deeper levels, also store in subtopics map to ensure tree updates
            this.p2SubTopicsMap[parentName] = topicNames;

            // Map these deeper level topics to their parent
            response.children.forEach(subtopic => {
              this.subtopicToTopicMap[subtopic.name_eng] = parentName;
            });
          }

          // Store topic IDs and metadata for all topics found
          response.children.forEach(topic => {
            // Determine if this topic should have children based on level
            let topicHasChildren = false;

            if (currentLevel === 0) {
              // For sections (level 0), they likely have topics (level 1)
              topicHasChildren = true;
            } else if (currentLevel === 1) {
              // For topics (level 1), they likely have subtopics (level 2)
              topicHasChildren = true;
            } else if (currentLevel === 2) {
              // For subtopics (level 2), they might have children but need explicit check
              // Use a more conservative approach - don't assume children unless we know
              topicHasChildren = false;
            } else {
              // For deeper levels, we don't assume children by default
              topicHasChildren = false;
            }

            // Set topic metadata with appropriate hasChildren flag
            this.topicIdMap[topic.name_eng] = {
              id: topic.topic_id,
              level: topic.item_level,
              hasChildren: topicHasChildren,
              hindiName: topic.name_hindi || undefined
            };

            // Log Hindi name if available
            if (topic.name_hindi) {
       //       console.log(`Topic with Hindi name: ${topic.name_eng} -> ${topic.name_hindi}`);
            }

        //    console.log(`Set topic metadata: ${topic.name_eng} (ID: ${topic.topic_id}, Level: ${topic.item_level}, HasChildren: ${topicHasChildren})`);

            // Also keep the legacy mappings for backward compatibility
            if (currentLevel === 0) {
              // Not used - subjects are at level 0
            } else if (currentLevel === 1) {
              this.level2TopicIdMap[topic.name_eng] = topic.topic_id;
            } else if (currentLevel === 2) {
              this.level3TopicIdMap[topic.name_eng] = topic.topic_id;
            }
          });

          // Emit appropriate event based on level
          if (currentLevel === 0) {
            this.onSectionsLoaded({subject: parentName, sections: topicNames});
          } else if (currentLevel === 1) {
            this.onTopicsLoaded({section: parentName, topics: topicNames});
          } else if (currentLevel >= 2) {
            // For level 2 and deeper, use the subtopics event
            this.onSubtopicsLoaded({topic: parentName, subtopics: topicNames});
          }

          // If there are no children at this level, load questions for this topic
          if (topicNames.length === 0) {
            this.loadQuestionsForTopic(parentName, parentId);
          }

          // Ensure Hindi names are loaded after processing topics
          setTimeout(() => {
            this.loadHindiNamesForUI();
          }, 500);
        },
        error: (error) => {
          console.error(`Error loading topics for ${parentName}:`, error);
        }
      });
  }

  // Load questions for a topic when it has no more children
  loadQuestionsForTopic(topicName: string, topicId: string): void {
    console.log(`Loading questions for topic: ${topicName} (ID: ${topicId})`);

    // Set loading state
    this.isLoadingTopicQuestions = true;

    // Important: We're not clearing loadedQuestions here, to avoid flickering
    // when transitioning from one subtopic with questions to another

    this.dataService.getQuestionsByTopicId(topicId)
      .subscribe({
        next: (questions) => {
          // Reset loading state
          this.isLoadingTopicQuestions = false;

          // Ensure questions is handled as an array
          if (!Array.isArray(questions)) {
            console.error(`API returned non-array response for topic: ${topicName}`, questions);
            // Only clear questions now that we know there are none
            this.loadedQuestions = [];
            this.selectedCard = null;
            return;
          }

          console.log(`Received ${questions.length} questions for topic: ${topicName}`);

          // Now we can safely update loadedQuestions without flickering
          this.loadedQuestions = questions;

          if (questions.length === 0) {
            console.log(`No questions found for topic: ${topicName}. Keeping main and details areas clear.`);
            // Ensure selected card is null to clear details area
            this.selectedCard = null;
          } else {
            console.log(`Successfully loaded ${questions.length} questions for topic: ${topicName}`);
          }
        },
        error: (error) => {
          // Reset loading state
          this.isLoadingTopicQuestions = false;

          console.error(`Error loading questions for topic ${topicName}:`, error);
          // Only clear questions on error
          this.loadedQuestions = [];
          // Ensure selected card is null to clear details area
          this.selectedCard = null;
        }
      });
  }

  // Add a new method to load the complete topic tree for a subject
  loadTopicTree(subjectName: string, subjectId: string): void {
    console.log(`Loading topic tree for subject: ${subjectName} with ID: ${subjectId}`);
    console.log(`currently stored subject: ${this.selectedSubject}`);
    console.log(`received topic tree for subject: ${subjectName} with ID: ${subjectId}`);
    this.isLoadingSubjects = true;

    // Clear previous data for this subject
    //this.clearTopicMapsForSubject(subjectName);

    // Use the new API that fetches all trees at once, but just for this single subject
    this.dataService.getAllTopicTrees([parseInt(subjectId)])
      .pipe(
        finalize(() => {
          this.isLoadingSubjects = false;
        })
      )
      .subscribe({
        next: (response) => {
          console.log(`Received topic tree response for ${subjectName}:`, response);

          if (response && response.trees && response.trees[subjectId]) {
            const treeData = response.trees[subjectId];
            // Process the tree and update all necessary data structures
            this.processTopicTree(treeData, subjectName);

            // Update the navigation with the processed data
            //this.refreshNavigationTree();

            console.log('Topic tree processing complete.');
          } else {
            console.error(`Empty or invalid tree response for ${subjectName}`);
          }
        },
        error: (error) => {
          console.error(`Error loading topic tree for ${subjectName}:`, error);
          // Reset loading indicators
          this.isLoadingSubjects = false;
        }
      });
  }

  // Clear existing topic maps when loading a new subject tree
  private clearTopicMapsForSubject(subjectName: string): void {
    console.log(`Clearing topic maps for subject: ${subjectName}`);

    // Clear subject-specific maps
    if (this.p1SubMap[subjectName]) {
      // Get sections for this subject
      const sections = [...this.p1SubMap[subjectName]];

      // Clear topics for each section
      sections.forEach(section => {
        if (this.p2TopicsMap[section]) {
          // Get topics for this section
          const topics = [...this.p2TopicsMap[section]];

          // Clear subtopics for each topic
          topics.forEach(topic => {
            delete this.p2SubTopicsMap[topic];
          });

          // Clear topics for this section
          delete this.p2TopicsMap[section];
        }
      });

      // Clear sections for this subject
      delete this.p1SubMap[subjectName];
    }

    console.log('Topic maps cleared');
  }

    // Process the topic tree and update data structures
  private processTopicTree(tree: TopicTreeNode, subjectName: string): void {
    // console.log(`Processing topic tree for ${subjectName}`, tree);

    // First, update the topicIdMap with all nodes in the tree
    this.populateTopicIdMapFromTree(tree);

    // Also update the Hindi name map
    this.updateHindiNamesFromTree(tree);

    // Populate sections (level 1) if the tree has children
    if (tree.children && tree.children.length > 0) {
      const sections = tree.children.map(node => node.name_eng);
      this.p1SubMap[subjectName] = sections;

      // For each section, populate topics and subtopics
      tree.children.forEach(sectionNode => {
        if (sectionNode.children && sectionNode.children.length > 0) {
          const sectionName = sectionNode.name_eng;
          const topics = sectionNode.children.map(topicNode => topicNode.name_eng);
          this.p2TopicsMap[sectionName] = topics;

          // For each topic, populate subtopics
          sectionNode.children.forEach(topicNode => {
            if (topicNode.children && topicNode.children.length > 0) {
              const topicName = topicNode.name_eng;
              const subtopics = topicNode.children.map(subtopicNode => subtopicNode.name_eng);
              this.p2SubTopicsMap[topicName] = subtopics;

              // Map subtopics back to their parent topics
              subtopics.forEach((subtopic, index) => {
                this.subtopicToTopicMap[subtopic] = topicName;

                // If this is a new subtopic with no children yet loaded,
                // check if it has children in the tree and update p2SubTopicsMap
                const subtopicNode = topicNode.children[index];
                if (subtopicNode && subtopicNode.children && subtopicNode.children.length > 0) {
                  const deeperSubtopics = subtopicNode.children.map(node => node.name_eng);
                  this.p2SubTopicsMap[subtopic] = deeperSubtopics;

                  // Map these deeper subtopics back to their parent
                  deeperSubtopics.forEach(deeperSubtopic => {
                    this.subtopicToTopicMap[deeperSubtopic] = subtopic;
                  });
                }
              });
            }
          });
        }
      });
    }

    // console.log('Updated topic maps:', {
    //   sections: this.p1SubMap[subjectName],
    //   topicsMap: this.p2TopicsMap,
    //   subtopicsMap: this.p2SubTopicsMap
    // });
  }

  // Populate the topicIdMap with all nodes from the tree
  private populateTopicIdMapFromTree(node: TopicTreeNode): void {
    // Add current node to the topicIdMap
    this.topicIdMap[node.name_eng] = {
      id: node.topic_id,
      level: node.item_level,
      hasChildren: node.children && node.children.length > 0,
      hindiName: node.name_hindi
    };

    // Recursively process children
    if (node.children && node.children.length > 0) {
      node.children.forEach(childNode => {
        this.populateTopicIdMapFromTree(childNode);
      });
    }
  }

  // Update Hindi names from the tree
  private updateHindiNamesFromTree(node: TopicTreeNode): void {
    // If the node has a Hindi name, update the hindi name map that will be passed to the navigation component
    if (node.name_hindi) {
      // Store Hindi names in a local map
      if (!this.navigationApp) {
        // If navigationApp is not yet available, store in topicIdMap for later use
        this.topicIdMap[node.name_eng] = {
          ...this.topicIdMap[node.name_eng],
          hindiName: node.name_hindi
        };
      } else {
        // Update the Hindi name in the navigation app component
        this.navigationApp.updateHindiNames({ [node.name_eng]: node.name_hindi });
      }
    }

    // Recursively process children
    if (node.children && node.children.length > 0) {
      node.children.forEach(childNode => {
        this.updateHindiNamesFromTree(childNode);
      });
    }
  }

  // Helper method to get subject ID
  private getSubjectId(subjectName: string): string {
    // Check if we have it in the subjectIdMap
    if (this.subjectIdMap[subjectName]) {
      return this.subjectIdMap[subjectName];
    }

    // Check if we have it in the topicIdMap
    if (this.topicIdMap[subjectName]) {
      return this.topicIdMap[subjectName].id;
    }

    return '';
  }

 loadAllSubjectTrees(): void {
    console.log('Loading all subject trees at startup');

    // Skip if we don't have any subjects
    if (!this.p1Subjects || this.p1Subjects.length === 0) {
      console.warn('No subjects available to load trees');
      return;
    }

    // Get all subject IDs
    const subjectIds = this.p1Subjects
      .map(subject => this.getSubjectId(subject))
      .filter(id => id !== '')
      .map(id => parseInt(id));

    if (subjectIds.length === 0) {
      console.warn('No valid subject IDs found to load trees');
      return;
    }

    console.log(`Fetching trees for ${subjectIds.length} subjects in a single API call`);

    // Use the new API to fetch all trees at once
    this.dataService.getAllTopicTrees(subjectIds).subscribe({
      next: (response) => {
        // console.log('All subject trees response received:', response);

        if (!response || !response.trees) {
          console.error('Invalid response from getAllTopicTrees API');
          return;
        }

        // Process each tree in the response
        let processedCount = 0;
        const totalSubjects = Object.keys(response.trees).length;

        Object.entries(response.trees).forEach(([subjectId, treeData]) => {
          if (!treeData) {
            console.warn(`No tree data for subject ID ${subjectId}`);
            processedCount++;
            return;
          }

          // Find the subject name for this ID
          const subjectName = this.p1Subjects.find(subject => this.getSubjectId(subject) === subjectId);
          if (!subjectName) {
            console.warn(`Could not find subject name for ID ${subjectId}`);
            processedCount++;
            return;
          }

          // console.log(`Processing tree for subject: ${subjectName} (ID: ${subjectId})`);

          // Process the tree to update our data structures
          this.processTopicTree(treeData, subjectName);

          // Ensure all topic-subtopic mappings are complete
          this.ensureTopicSubtopicMappings(treeData);

          processedCount++;

          // Check if we've processed all trees
          if (processedCount === totalSubjects) {
            console.log('All subject trees processed successfully');

            // Add longer delay to ensure DOM updates are complete
            setTimeout(() => {
              // Refresh the navigation tree once all data is loaded
              this.refreshNavigationTree();

              // Add explicit call to ensure tree expands
              this.expandTreeAfterLoad();

              // Auto-select the first subject if none is selected
              if (!this.selectedSubject && this.p1Subjects.length > 0) {
                this.onSubjectSelect(this.p1Subjects[0]);
              }

              // Force a manual update in the SingleTreeComponent
              if (this.singleTreeComponent) {
                this.singleTreeComponent.updateTreeData();
                setTimeout(() => {
                  this.singleTreeComponent.expandAllNodes();
                }, 200);
              }

              // Force UI update
              this.cdr.detectChanges();
            }, 500); // Increase timeout to 500ms
          }
        });
      },
      error: (error) => {
        console.error('Error loading subject trees:', error);
      }
    });
  }

  // Ensure that all topic-subtopic mappings are correctly established
  private ensureTopicSubtopicMappings(tree: TopicTreeNode): void {
    if (!tree.children || tree.children.length === 0) return;

    // Process level 1 (sections)
    tree.children.forEach(sectionNode => {
      if (!sectionNode.children || sectionNode.children.length === 0) return;

      // Process level 2 (topics)
      sectionNode.children.forEach(topicNode => {
        const topicName = topicNode.name_eng;

        // Make sure this topic is registered as having children if it does
        if (topicNode.children && topicNode.children.length > 0) {
          // Ensure the topic is marked as having children
          if (this.topicIdMap[topicName]) {
            this.topicIdMap[topicName].hasChildren = true;
          }

          // Ensure subtopics are properly mapped to this topic
          const subtopics = topicNode.children.map(st => st.name_eng);
          this.p2SubTopicsMap[topicName] = subtopics;

          // Map subtopics back to their parent topic
          subtopics.forEach(subtopic => {
            this.subtopicToTopicMap[subtopic] = topicName;
          });
        }
      });
    });
  }


  // Improved method to load Hindi names from the processed topic trees
  loadHindiNamesFromTopicMaps(): void {
    console.log('Loading Hindi names from processed topic trees');

    // Collect all Hindi names from the topicIdMap
    const hindiNames: { [key: string]: string } = {};

    // Extract Hindi names from topicIdMap
    Object.entries(this.topicIdMap).forEach(([topicName, topicInfo]) => {
      if (topicInfo.hindiName) {
        hindiNames[topicName] = topicInfo.hindiName;
      }
    });

    console.log(`Collected ${Object.keys(hindiNames).length} Hindi names to update in navigation component`);

    // Update the navigation component with all collected Hindi names at once
    if (this.navigationApp) {
      this.navigationApp.updateHindiNames(hindiNames);
      console.log('Updated Hindi names in navigation component');
    } else {
      console.warn('Navigation component not available to update Hindi names');
    }
  }

  // Override ngAfterViewInit to make sure we update Hindi names after the navigation component is available
  ngAfterViewInit(): void {
    console.log('AppHomeComponent ngAfterViewInit called');

    // Initialize the mobile view check after the view is initialized
    this.checkIfMobileView();

    // Make sure details area is not visible by default on mobile
    if (this.isMobileView) {
      this.showMobileDetails = false;
    }

    // Check if SingleTreeComponent is available
    console.log('Checking single tree component availability in ngAfterViewInit:',
                this.singleTreeComponent ? 'AVAILABLE' : 'NOT AVAILABLE');

    // Update child components with user role
    this.updateChildComponentsWithUserRole();

    // Wait a bit to ensure components are initialized
    setTimeout(() => {
      // Initialize the navigation component with Hindi names from our topic maps
      if (this.navigationApp) {
        console.log('Navigation component found, updating Hindi names');
        this.loadHindiNamesFromTopicMaps();
      } else {
        console.warn('Navigation component not available in ngAfterViewInit');
      }

      // Ensure tree is expanded after view is initialized
      if (this.singleTreeComponent) {
        console.log('Single tree component found in ngAfterViewInit, expanding nodes');
        // Use try-catch to handle any errors during expansion
        try {
          this.singleTreeComponent.updateTreeData();
          this.singleTreeComponent.expandAllNodes();

          // Refresh the navigation view
          this.refreshNavigationTree();

          console.log('Tree expansion in ngAfterViewInit completed successfully');
        } catch (err) {
          console.error('Error expanding tree in ngAfterViewInit:', err);
        }
      } else {
        console.warn('Single tree component not available in ngAfterViewInit, ' +
                    'will rely on expandTreeAfterLoad retry mechanism');
      }
    }, 1000);

    // Listen for window resize events
    window.addEventListener('resize', this.onWindowResize.bind(this));

    // Register event listeners for resizing
    this.registerEventListeners();

    // Change detection might be needed
    this.cdr.detectChanges();
  }

  // Add a method to handle layout changes
  onLayoutChange(layoutId: string): void {
    console.log('Layout changed to:', layoutId);

    // Handle special mobile navigation toggle event
    if (layoutId === 'toggle_mobile_nav' && this.isMobileView) {
      this.toggleMobileNav();
      return;
    }

    // For mobile view, always use single_tree layout regardless of what was clicked
    if (this.isMobileView) {
      layoutId = 'single_tree';
    }

    // Always use single tree layout
    this.showSingleTree = true;

    // Set proper layout proportions: 20% for tree, 75% for main, 25% for details
    this.mainAreaFlex = '0 0 75%';
    this.detailsAreaFlex = '0 0 25%';
    this.navigationAreaFlex = '0 0 20%';
    this.currentNavWidth = 20;

    // Update the DOM to reflect new widths
    setTimeout(() => {
      const singleTree = document.querySelector('app-single-tree');
      if (singleTree) {
        (singleTree as HTMLElement).style.flex = `0 0 ${this.currentNavWidth}%`;
        (singleTree as HTMLElement).style.minWidth = `${this.currentNavWidth}%`;
      }
    }, 100);

    // Show both main and details areas by default
    this.showDetails = true;
    this.showMains = true;

    // Force a refresh of the tree component
    setTimeout(() => {
      if (this.singleTreeComponent) {
        this.singleTreeComponent.updateTreeData();
        this.singleTreeComponent.expandAllNodes();
        this.refreshNavigationTree();
      }

      // Force resize event to update all components
      window.dispatchEvent(new Event('resize'));
    }, 300);

    // Ensure Hindi names are loaded for the single tree
    this.loadHindiNamesForUI();
  }

  // Handle search term changes from layout-switcher component
  onSearchTermChange(searchOptions: { term: string, filterMode: boolean }): void {
    console.log('AppHome: onSearchTermChange called with:', searchOptions);

    // Make sure we're in single tree mode when searching
    if (!this.showSingleTree) {
      console.log('AppHome: Switching to single tree mode for search');
      this.onLayoutChange('single_tree');

      // Allow a moment for the component to be rendered before applying search
      // This timeout is now coordinated with the layout-switcher's ngOnChanges
      setTimeout(() => {
        this.applySearchToSingleTree(searchOptions);
      }, 500);
    } else {
      // Already in single tree mode, apply search immediately
      this.applySearchToSingleTree(searchOptions);
    }
  }

  // Helper method to apply search to the single tree component
  private applySearchToSingleTree(searchOptions: { term: string, filterMode: boolean }): void {
    if (this.singleTreeComponent) {
      console.log('AppHome: Applying search to singleTreeComponent', searchOptions);

      // Update the search parameters
      this.singleTreeComponent.searchTerm = searchOptions.term;
      this.singleTreeComponent.filterMode = searchOptions.filterMode;

      // Explicitly call performSearch() to ensure the search is executed
      this.singleTreeComponent.performSearch();

      // Force a double-check after a small delay to ensure highlighting and filtering
      setTimeout(() => {
        if (this.singleTreeComponent &&
            (this.singleTreeComponent.searchTerm !== searchOptions.term ||
             this.singleTreeComponent.filterMode !== searchOptions.filterMode)) {
          console.log('AppHome: Reapplying search because parameters were out of sync');
          this.singleTreeComponent.searchTerm = searchOptions.term;
          this.singleTreeComponent.filterMode = searchOptions.filterMode;
          this.singleTreeComponent.performSearch();
        }
      }, 300);
    } else {
      console.warn('AppHome: singleTreeComponent is not available for search!');
    }
  }

  // Handle show details change from main area component
  onShowDetailsChange(showDetails: boolean): void {
    this.showDetails = showDetails;
    // Adjust main-area size when details pane toggles
    if (showDetails) {
      this.mainAreaFlex = '0 0 60%';
      this.detailsAreaFlex = '1 1 auto';
    } else {
      this.mainAreaFlex = '1 1 auto';
    }
    // Give the UI time to update when details pane visibility changes
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
  }

  // Handle horizontal view toggle from single-tree component
  onHorizontalViewChange(showHorizontal: boolean): void {
    console.log('App Home - onHorizontalViewChange called with:', showHorizontal);
    this.showHorizontalTabs = showHorizontal;
    this.cdr.detectChanges(); // Force change detection
    
    // Update flex values based on horizontal view state
    if (showHorizontal) {
      // Make single-tree-content full width when horizontal tabs are shown
      this.mainAreaFlex = '1 1 100%';
      this.detailsAreaFlex = '0 0 0%';
    } else {
      // Restore normal layout when returning to tree view
      this.loadPanelSizes();
    }
  }

  /**
   * Handle blog mode change from single-tree component
   */
  onBlogModeChange(blogMode: boolean): void {
    console.log('App Home - onBlogModeChange called with:', blogMode);
    console.log('App Home - Current selectedTopic:', this.selectedTopic);
    this.blogMode = blogMode;
    
    if (blogMode) {
      // When blog mode is enabled, collapse main area and show blog-home
      this.mainAreaFlex = '0 0 0%';
      this.showMains = false;
      
      // In blog mode: blog-home gets 75%, details-area gets 25% (but resizable)
      // Force details to show in blog mode for better user experience
      this.showDetails = true;
      this.blogHomeAreaFlex = '0 0 75%';
      this.blogDetailsAreaFlex = '0 0 25%';
      
      console.log('Blog mode enabled: blog-home at 75%, details-area at 25%');
      console.log('App Home - Current selectedTopic that will be passed to blog-home:', this.selectedTopic);
      
      // Force change detection to create the blog-home component and trigger input binding
      this.cdr.detectChanges();
      
      // After a short delay, verify the blog-home component exists and log its state
      setTimeout(() => {
        if (this.blogHomeComponent) {
          console.log('App Home - Blog-home component is available');
          console.log('App Home - Blog-home selectedTopicFromTree:', this.blogHomeComponent.selectedTopicFromTree);
          console.log('App Home - Blog-home isBlogMode:', this.blogHomeComponent.isBlogMode);
          
          // Trigger another change detection to ensure inputs are updated
          this.cdr.detectChanges();
        } else {
          console.warn('App Home - Blog-home component not available after timeout');
        }
      }, 200);
    } else {
      // When blog mode is disabled, restore normal layout
      this.loadPanelSizes();
      this.showMains = true;
      
      console.log('Blog mode disabled: restored normal layout');
    }
    
    this.cdr.detectChanges(); // Force change detection
  }

  /**
   * Handle change nav view toggle from single-tree component
   */
  onChangeNavViewChange(changeNavView: boolean): void {
    console.log('App Home - onChangeNavViewChange called with:', changeNavView);
    this.changeNavView = changeNavView;
    
    // Update the blog-home component if it exists
    if (this.blogHomeComponent) {
      this.blogHomeComponent.isFloatingHeaderMode = changeNavView;
      console.log('App Home - Updated blog-home floating header mode to:', changeNavView);
    }
    
    this.cdr.detectChanges(); // Force change detection
  }

  // Handle closing details window from details area component
  onHideDetails(): void {
    // Set showDetails to false to hide the details area
    this.showDetails = false;
    // Reset the selected card
    this.selectedCard = null;

    if (this.mainAreaComponent) {
      // Directly set the showDetails property in the main area component
      this.mainAreaComponent.showDetails = false;
      // The component will handle its own change detection
    }

    // Force a change detection on the entire app to update UI
    this.cdr.detectChanges();

    // Give the UI time to update
    setTimeout(() => {
      // Force another detection update after a short delay
      this.cdr.detectChanges();
      window.dispatchEvent(new Event('resize'));
    }, 100);
  }

  // New function for UI components to request Hindi names
  loadHindiNamesForUI(): void {
    console.log('Loading Hindi names for UI components');

    // Prevent executing this multiple times in rapid succession
    if (this._isLoadingHindiNames) {
      console.log('Hindi name loading already in progress, skipping duplicate request');
      return;
    }

    this._isLoadingHindiNames = true;

    // Use setTimeout to avoid blocking the UI thread
    setTimeout(() => {
      try {
        // Start with basic hardcoded names for common subjects
        const basicHindiNames = {
          'Mathematics': 'गणित',
          'Physics': 'भौतिकी',
          'Chemistry': 'रसायन विज्ञान',
          'Computer Science': 'कंप्यूटर विज्ञान',
          'Algebra': 'बीजगणित',
          'Calculus': 'कैलकुलस',
          'Mechanics': 'यांत्रिकी',
          'Optics': 'प्रकाशिकी',
          'Electronics': 'इलेक्ट्रॉनिक्स',
          'History': 'इतिहास',
          'Geography': 'भूगोल',
          'Science': 'विज्ञान',
          'Polity': 'राजव्यवस्था'
        };

        // Process the topicIdMap in smaller batches
        this.processHindiNamesInBatches(basicHindiNames);
      } catch (error) {
        console.error('Error loading Hindi names:', error);
        this._isLoadingHindiNames = false;
      }
    }, 100);
  }

  // Process Hindi names in smaller batches to avoid freezing the UI
  private _isLoadingHindiNames = false;
  private processHindiNamesInBatches(initialNames: {[key: string]: string}): void {
    // Start with initial names
    const allHindiNames = {...initialNames};

    // Extract topic names from topicIdMap
    const topicNames = Object.keys(this.topicIdMap);
    const batchSize = 50; // Process 50 names at a time
    let currentIndex = 0;

    const processNextBatch = () => {
      // Calculate end index for current batch
      const endIndex = Math.min(currentIndex + batchSize, topicNames.length);
      console.log(`Processing Hindi names batch ${currentIndex}-${endIndex} of ${topicNames.length} total names`);

      // Process this batch
      for (let i = currentIndex; i < endIndex; i++) {
        const topicName = topicNames[i];
        const topicInfo = this.topicIdMap[topicName];

        if (topicInfo && topicInfo.hindiName) {
          allHindiNames[topicName] = topicInfo.hindiName;
        }
      }

      // Update current index
      currentIndex = endIndex;

      // If we have more batches to process, schedule next batch
      if (currentIndex < topicNames.length) {
        setTimeout(() => processNextBatch(), 0);
      } else {
        // We're done, update the hindi name map and navigation component
        this.hindiNameMap = allHindiNames;
        console.log('Hindi names processed:', Object.keys(this.hindiNameMap).length);

        // Update the navigation component with Hindi names if available
        if (this.navigationApp) {
          this.navigationApp.updateHindiNames(this.hindiNameMap);
        }

        // Clear the loading flag
        this._isLoadingHindiNames = false;
      }
    };

    // Start the first batch
    processNextBatch();
  }

  // Handle view mode changes from the main-area component
  onViewModeChange(viewMode: string): void {
    console.log('View mode changed to:', viewMode);

    // Store the current selected card to preserve it during layout changes
    const currentSelectedCard = this.selectedCard;
    const wasFullscreen = this.mainAreaFlex === '0 0 0%';
    const wasPopup = this.mainAreaFlex === '0 0 12.5%';

    switch (viewMode) {
      case 'split':
        // Split view - default layout with exactly 75% main and 25% details
        this.mainAreaFlex = '0 0 75%';
        this.detailsAreaFlex = '0 0 25%';
        this.showMains = true; // Ensure main area is shown in split view
        break;
      case 'large':
        // Large view - main area at 30%, details area at 70%
        this.mainAreaFlex = '0 0 30%';
        this.detailsAreaFlex = '0 0 70%';
        this.showMains = true; // Ensure main area is shown in large view
        break;
      case 'popup':
        // Pop out view - main area minimized, details area appears as dialog
        this.mainAreaFlex = '0 0 12.5%';
        this.detailsAreaFlex = '0 0 75%';
        this.showMains = true; // Keep main content visible behind popup

        // Force popup to be on top of content
        document.body.style.overflow = 'hidden'; // Prevent body scrolling while popup is shown

        // Update the details component's internal viewMode and popup state
        setTimeout(() => {
          const detailsElement = document.querySelector('app-details-area.popup-mode');
          if (detailsElement) {
            try {
              // Access the component's instance using Angular's __ngContext__
              const componentInstance = (detailsElement as any).__ngContext__?.instance;
              if (componentInstance) {
                console.log('Setting popup-mode details component viewMode to popup');
                componentInstance.viewMode = 'popup';

                // Update the popup class tracking flag if it exists
                if (typeof componentInstance.updatePopupClassState === 'function') {
                  componentInstance.updatePopupClassState(true);
                }

                componentInstance.cdr.detectChanges();
              }
            } catch (e) {
              console.error('Error updating details component viewMode:', e);
            }
          }
        }, 10);
        break;
      case 'fullscreen':
        // Full screen - collapse main area, details takes full width
        this.mainAreaFlex = '0 0 0%';
        this.detailsAreaFlex = '1 1 100%';
        this.showMains = false; // Set showMains to false by default in fullscreen mode
        break;
    }

    // Explicitly update current width values
    if (viewMode === 'split') {
      this.currentMainWidth = 75;
    } else if (viewMode === 'large') {
      this.currentMainWidth = 30;
    }

    // Force a resize event to update all components
    setTimeout(() => {
      // If we were in fullscreen or popup mode and now we're showing the main area,
      // make sure the selected card is still selected
      if ((wasFullscreen || wasPopup) && (viewMode === 'split' || viewMode === 'large') && currentSelectedCard) {
        console.log('Restoring selected card after exiting fullscreen/popup mode');
        // Re-select the card to ensure the selection is preserved
        this.selectedCard = null;
        setTimeout(() => {
          this.selectedCard = currentSelectedCard;
        }, 50);
      }

      // If we're exiting popup mode, restore body scroll
      if (wasPopup && viewMode !== 'popup') {
        document.body.style.overflow = '';
      }

      window.dispatchEvent(new Event('resize'));
    }, 100);
  }

  // Handle show mains change from details area component
  onShowMainsChange(showMains: boolean): void {
    console.log('Show mains changed to:', showMains);

    // Store the current selected card to preserve it during layout changes
    const currentSelectedCard = this.selectedCard;

    this.showMains = showMains;

    // If show mains is true and we're in fullscreen mode, restore the main area
    if (showMains && this.mainAreaFlex === '0 0 0%') {
      // Restore to a split view with main area visible
      this.mainAreaFlex = '0 0 60%';
      this.detailsAreaFlex = '0 0 40%';
    }

    // Give the UI time to update
    setTimeout(() => {
      // Ensure the selected card is still selected after layout changes
      if (currentSelectedCard && this.selectedCard !== currentSelectedCard) {
        this.selectedCard = currentSelectedCard;
      }
      window.dispatchEvent(new Event('resize'));
    }, 100);
  }

  // Check if in mobile view based on screen width
  private checkIfMobileView(): void {
    this.isMobileView = window.innerWidth <= 768;

    // In mobile view, show only the single tree when navigation is visible
    if (this.isMobileView) {
      this.showSingleTree = true;
      // Don't reset mobile details on initial check - only hide mobile nav
      this.showMobileNav = false;

      // Ensure data is loaded for mobile view
      if ((!this.p1Subjects || this.p1Subjects.length === 0) && !this.isLoadingSubjects) {
        console.log('Mobile view detected - initiating subject data loading');
        this.loadSubjectsFromApi();
      }

      // When in mobile view, ensure tree data is loaded and expanded
      setTimeout(() => {
        if (this.singleTreeComponent) {
          console.log('Mobile view detected - ensuring tree data is updated and expanded');
          this.singleTreeComponent.updateTreeData();
          this.singleTreeComponent.expandAllNodes();
          // Also expand yearwise tree if in that mode
          if (this.p1Mode === 'yearwise') {
            this.singleTreeComponent.updateYearwiseTreeData();
            this.singleTreeComponent.expandAllYearwiseNodes();
          }
        }
      }, 500);

      // We don't reset showMobileDetails here because it would prevent details from showing
      // when clicking on a question
    }
  }

  // Toggle mobile navigation visibility
  toggleMobileNav(): void {
    // Toggle the mobile navigation state
    this.showMobileNav = !this.showMobileNav;

    // When showing mobile nav, hide details view
    if (this.showMobileNav) {
      this.showMobileDetails = false;
    }

    console.log(`Mobile navigation toggled: ${this.showMobileNav ? 'showing' : 'hiding'} navigation`);
    console.log('Current mobile view state - Nav visible:', this.showMobileNav,
                'Details visible:', this.showMobileDetails);
  }

  // Toggle mobile details visibility
  toggleMobileDetails(show: boolean): void {
    console.log(`Toggling mobile details visibility: ${show ? 'showing' : 'hiding'} details`);
    console.log('Current view state - Mobile view:', this.isMobileView,
                'Nav visible:', this.showMobileNav,
                'Details visible:', this.showMobileDetails);

    this.showMobileDetails = show;

    // When showing details, hide nav
    if (this.showMobileDetails) {
      this.showMobileNav = false;
    }

    console.log('New view state - Mobile view:', this.isMobileView,
               'Nav visible:', this.showMobileNav,
               'Details visible:', this.showMobileDetails);
  }

  // Back to main area from details on mobile
  backToMainFromDetails(): void {
    this.showMobileDetails = false;
  }

  // Add HostListener for window resize
  @HostListener('window:resize')
  onResize() {
    this.onWindowResize();
  }

  // Handle language change
  onLanguageChange(language: string): void {
    // Handle language change
    console.log('Language changed to:', language);

    // Store the language value in a consistent format to prevent case issues
    let storageValue = language;

    // Normalize the language value for storage
    if (language.toLowerCase().includes('english')) {
      storageValue = 'englishOnly';
    } else if (language.toLowerCase().includes('hindi')) {
      storageValue = 'hindiOnly';
    } else {
      storageValue = 'bilingual';
    }

    // Save language preference to localStorage
    localStorage.setItem('languagePreference', storageValue);
    console.log('Saved language preference to localStorage:', storageValue);

    // Update language in navigation component if available
    if (this.navigationApp) {
      this.navigationApp.selectedLanguage = language;
      this.navigationApp.onLanguageChange(language);
    }

    // Update language in single-tree component if available
    if (this.singleTreeComponent) {
      this.singleTreeComponent.selectedLanguage = language;
      // No need to call onLanguageChange directly as it was the source of this event
    }

    // Update language in main area component if available
    if (this.mainAreaComponent) {
      this.mainAreaComponent.selectedLanguage = storageValue;
      this.mainAreaComponent.onLanguageChange();
    }

    // If Hindi names aren't loaded yet, load them
    if (language.toLowerCase().includes('hindi') || language.toLowerCase() === 'bilingual') {
      this.loadHindiNamesForUI();
    }
  }

  // Update the navigation path from hierarchy path
  onHierarchyPathChange(pathItems: string[]): void {
    console.log('Hierarchy path changed:', pathItems);

    // Convert string array to NavigationPathItem objects
    const navigationItems = pathItems.map((name, index) => {
      let type: 'subject' | 'section' | 'topic' | 'subtopic' | 'exam' | 'paper' | 'year' = 'subject'; // Default

      // Determine the type based on position and mode
      if (this.p1Mode === 'topicwise') {
        if (index === 0) type = 'subject';
        else if (index === 1) type = 'section';
        else if (index === 2) type = 'topic';
        else if (index === 3) type = 'subtopic';
      } else { // yearwise
        if (index === 0) type = 'exam';
        else if (index === 1) type = 'paper';
        else if (index === 2) type = 'year';
      }

      // Get hindi name from map if available
      const hindiName = this.hindiNameMap[name] || '';

      return { name, type, hindiName };
    });

    // Update the navigation path service
    this.navigationPathService.updateNavigationPath(navigationItems);
  }

  // Add missing event listener registration methods
  private registerEventListeners(): void {
    console.log('Registering event listeners');
    window.addEventListener('resize', this.onWindowResize.bind(this));
  }

  private unregisterEventListeners(): void {
    console.log('Unregistering event listeners');
    window.removeEventListener('resize', this.onWindowResize.bind(this));

    // Clean up main divider event listeners if still active
    if (this.isDragging) {
      document.removeEventListener('mousemove', this.mouseMoveHandler);
      document.removeEventListener('mouseup', this.mouseUpHandler);
      this.isDragging = false;
    }

    // Clean up nav divider event listeners if still active
    if (this.isNavDragging) {
      document.removeEventListener('mousemove', this.navMouseMoveHandler);
      document.removeEventListener('mouseup', this.navMouseUpHandler);
      this.isNavDragging = false;
    }
  }

  private onWindowResize(): void {
    this.checkIfMobileView();
  }

  // Handle mouse down event on vertical divider
  onMouseDown(event: MouseEvent): void {
    this.isDragging = true;
    event.preventDefault();

    // Store the starting position
    this.startX = event.clientX;

    if (this.blogMode) {
      // Get the current blog-home width for blog mode
      const blogWidth = parseFloat(this.blogHomeAreaFlex.split(' ')[2]);
      this.currentMainWidth = isNaN(blogWidth) ? 75 : blogWidth;
    } else {
      // Get the current main area width for normal mode
      const mainWidth = parseFloat(this.mainAreaFlex.split(' ')[2]);
      this.currentMainWidth = isNaN(mainWidth) ? 60 : mainWidth;
    }

    // Add event listeners for mouse move and up
    document.addEventListener('mousemove', this.mouseMoveHandler);
    document.addEventListener('mouseup', this.mouseUpHandler);
  }

  // Handle mouse move during resize
  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;

    // Calculate how much the mouse has moved
    const dx = event.clientX - this.startX;

    // Get the container width
    const container = document.querySelector('.single-tree-content');
    if (!container) return;
    const containerWidth = container.getBoundingClientRect().width;

    // Convert mouse movement to percentage of container width
    const percentChange = (dx / containerWidth) * 100;

    if (this.blogMode) {
      // Handle blog mode resizing (blog-home vs details-area)
      const currentBlogWidth = parseFloat(this.blogHomeAreaFlex.split(' ')[2]);
      const originalBlogWidth = isNaN(currentBlogWidth) ? 75 : currentBlogWidth;
      
      // Calculate new width based on the original width plus the change
      let newBlogWidth = originalBlogWidth + percentChange;
      
      // Ensure the width is within bounds (20% to 80% for blog mode)
      const constrainedBlogWidth = Math.max(20, Math.min(newBlogWidth, 80));
      
      // Update blog flex values
      this.blogHomeAreaFlex = `0 0 ${constrainedBlogWidth.toFixed(2)}%`;
      this.blogDetailsAreaFlex = `0 0 ${(100 - constrainedBlogWidth).toFixed(2)}%`;
    } else {
      // Handle normal mode resizing (main-area vs details-area)
      // Calculate new width based on the original width plus the change
      let newMainWidth = this.currentMainWidth + percentChange;

      // Ensure the width is within bounds
      const constrainedWidth = Math.max(
        this.MIN_MAIN_WIDTH,
        Math.min(newMainWidth, this.MAX_MAIN_WIDTH)
      );

      // Update flex values
      this.mainAreaFlex = `0 0 ${constrainedWidth.toFixed(2)}%`;
      this.detailsAreaFlex = `0 0 ${(100 - constrainedWidth).toFixed(2)}%`;
    }
  }

  // Handle mouse up to end resize
  onMouseUp(): void {
    if (!this.isDragging) return;

    this.isDragging = false;

    // Remove event listeners
    document.removeEventListener('mousemove', this.mouseMoveHandler);
    document.removeEventListener('mouseup', this.mouseUpHandler);

    if (this.blogMode) {
      // Handle blog mode cleanup
      const blogWidth = parseFloat(this.blogHomeAreaFlex.split(' ')[2]);
      if (!isNaN(blogWidth)) {
        // Update the current width so future drags start from this position
        this.currentMainWidth = blogWidth;

        // Ensure the width is within bounds (20% to 80% for blog mode)
        const constrainedBlogWidth = Math.max(20, Math.min(blogWidth, 80));

        // Update with constrained and formatted values
        this.blogHomeAreaFlex = `0 0 ${constrainedBlogWidth.toFixed(2)}%`;
        this.blogDetailsAreaFlex = `0 0 ${(100 - constrainedBlogWidth).toFixed(2)}%`;
      }
      
      // Save blog mode panel sizes
      this.saveBlogPanelSizes();
    } else {
      // Handle normal mode cleanup
      const mainWidth = parseFloat(this.mainAreaFlex.split(' ')[2]);
      if (!isNaN(mainWidth)) {
        // Update the current width so future drags start from this position
        this.currentMainWidth = mainWidth;

        // Ensure the width is within bounds
        const constrainedWidth = Math.max(
          this.MIN_MAIN_WIDTH,
          Math.min(mainWidth, this.MAX_MAIN_WIDTH)
        );

        // Update with constrained and formatted values
        this.mainAreaFlex = `0 0 ${constrainedWidth.toFixed(2)}%`;
        this.detailsAreaFlex = `0 0 ${(100 - constrainedWidth).toFixed(2)}%`;
      }

      // Save the new panel sizes
      this.savePanelSizes();
    }
  }

  // Event handler properties
  private mouseMoveHandler = (event: MouseEvent) => this.onMouseMove(event);
  private mouseUpHandler = () => this.onMouseUp();

  // Add the onShowAllBundles method to the component
  onShowAllBundles(): void {
    console.log('Handling show all bundles request from single-tree');

    // Reset selections
    this.selectedSubject = null;
    this.selectedSubItem = null;
    this.selectedTopic = null;
    this.selectedSubTopic = null;
    this.selectedYearwiseExam = null;
    this.selectedYearwiseLower = null;
    this.selectedYear = null;

    // Reset navigation path
    this.navigationPathService.clearNavigationPath();

    // Tell main-area component to reload all bundles
    if (this.mainAreaComponent) {
      setTimeout(() => {
        this.mainAreaComponent.loadFileBundle();
      }, 0);
    }

    // Ensure preloader is turned off
    this.isPreloading = false;
  }

  // Handle admin mode change from SingleTreeComponent
  onAdminModeChange(isAdmin: boolean): void {
    console.log('Admin mode changed to:', isAdmin);

    // Update file processing component to show/hide admin features
    if (this.mainAreaComponent) {
      this.mainAreaComponent.setAdminMode(isAdmin);
    }
  }

  // Handle subjects refreshed event from SingleTreeComponent
  onSubjectsRefreshed(): void {
    console.log('Subjects refreshed successfully');

    // Get updated subjects and maps from single-tree component
    if (this.singleTreeComponent) {
      // Update local arrays and maps with the ones from single-tree
      this.p1Subjects = [...this.singleTreeComponent.p1Subjects];
      this.p1SubMap = {...this.singleTreeComponent.p1SubMap};

      console.log('Updated p1Subjects:', this.p1Subjects);
      console.log('Updated p1SubMap:', this.p1SubMap);
    }

    // Reset selections
    this.selectedSubject = null;
    this.selectedSubItem = null;
    this.selectedTopic = null;
    this.selectedSubTopic = null;
    this.selectedYearwiseExam = null;
    this.selectedYearwiseLower = null;
    this.selectedYear = null;

    // Reset navigation path
    this.navigationPathService.clearNavigationPath();

    // Update the main area to show default content
    if (this.mainAreaComponent) {
      this.mainAreaComponent.loadFileBundle();
    }

    // Force change detection to ensure UI updates
    this.cdr.detectChanges();

    // Show a success notification
    this.showResetNotification = true;
    setTimeout(() => {
      this.showResetNotification = false;
      this.cdr.detectChanges();
    }, 3000);
  }

  // Handle mouse down event on navigation divider
  onNavDividerMouseDown(event: MouseEvent): void {
    this.isNavDragging = true;
    event.preventDefault();

    // Store the starting position
    this.navStartX = event.clientX;

    // Get single-tree component's current width
    const singleTree = document.querySelector('app-single-tree');
    if (singleTree) {
      const styles = window.getComputedStyle(singleTree);
      const width = parseFloat(styles.width) / window.innerWidth * 100;
      this.currentNavWidth = isNaN(width) ? 30 : width;
    }

    // Add event listeners for mouse move and up
    document.addEventListener('mousemove', this.navMouseMoveHandler);
    document.addEventListener('mouseup', this.navMouseUpHandler);
  }

  // Handle mouse move during nav resize
  onNavMouseMove(event: MouseEvent): void {
    if (!this.isNavDragging) return;

    // Calculate how much the mouse has moved
    const dx = event.clientX - this.navStartX;

    // Convert mouse movement to percentage of window width
    const percentChange = (dx / window.innerWidth) * 100;

    // Calculate new width based on the original width plus the change
    let newNavWidth = this.currentNavWidth + percentChange;

    // Ensure the width is within bounds (20% to 50%)
    const constrainedWidth = Math.max(
      this.MIN_NAV_WIDTH,
      Math.min(newNavWidth, this.MAX_NAV_WIDTH)
    );

    // Update single-tree width
    const singleTree = document.querySelector('app-single-tree');
    if (singleTree) {
      (singleTree as HTMLElement).style.flex = `0 0 ${constrainedWidth.toFixed(2)}%`;
      (singleTree as HTMLElement).style.minWidth = `${constrainedWidth.toFixed(2)}%`;
    }

    // Force change detection
    this.cdr.detectChanges();
  }

  // Handle mouse up to end nav resize
  onNavMouseUp(): void {
    if (!this.isNavDragging) return;

    this.isNavDragging = false;

    // Remove event listeners
    document.removeEventListener('mousemove', this.navMouseMoveHandler);
    document.removeEventListener('mouseup', this.navMouseUpHandler);

    // Update the current width for future reference
    const singleTree = document.querySelector('app-single-tree');
    if (singleTree) {
      const styles = window.getComputedStyle(singleTree);
      const width = parseFloat(styles.width) / window.innerWidth * 100;
      if (!isNaN(width)) {
        this.currentNavWidth = width;

        // Update the single-tree component with the new width
        (singleTree as HTMLElement).style.flex = `0 0 ${this.currentNavWidth}%`;
        (singleTree as HTMLElement).style.minWidth = `${this.currentNavWidth}%`;

        // Also update the navigation area flex property
        this.navigationAreaFlex = `0 0 ${this.currentNavWidth}%`;
      }
    }

    // Save the new navigation panel size
    this.saveNavPanelSize();
  }

  // Event handler properties for nav resizing
  private navMouseMoveHandler = (event: MouseEvent) => this.onNavMouseMove(event);
  private navMouseUpHandler = () => this.onNavMouseUp();

  // Add the expandTreeAfterLoad method that was removed
  private expandTreeAfterLoad(): void {
    // console.log('Expanding tree after initial load');

    // Track retry attempts
    const maxRetries = 5;
    let retryCount = 0;

    const tryExpandTree = () => {
      retryCount++;
      // console.log(`Tree expansion attempt ${retryCount} of ${maxRetries}`);

      // Use ViewChild.changes to detect when component becomes available
      if (this.singleTreeComponent) {
        // console.log('Single tree component found, expanding nodes');
        try {
          // First update the data
          this.singleTreeComponent.updateTreeData();

          // Then expand the nodes with a small delay to ensure data is processed
          setTimeout(() => {
            try {
              // Try to expand the tree
              this.singleTreeComponent.expandAllNodes();

              // Also expand yearwise tree if in that mode
              if (this.p1Mode === 'yearwise') {
                this.singleTreeComponent.updateYearwiseTreeData();
                this.singleTreeComponent.expandAllYearwiseNodes();
              }

              // Force refresh of tree display
              this.refreshNavigationTree();

              // Force change detection to update UI
              this.cdr.detectChanges();

              // console.log('Tree expansion completed successfully');
            } catch (innerErr) {
              console.error('Error in delayed tree expansion:', innerErr);

              // Retry if we haven't reached max attempts
              if (retryCount < maxRetries) {
                setTimeout(tryExpandTree, 500);
              }
            }
          }, 300);
        } catch (err) {
          console.error('Error updating tree data:', err);

          // Retry if we haven't reached max attempts
          if (retryCount < maxRetries) {
            setTimeout(tryExpandTree, 500);
          }
        }
      } else {
        console.warn('Single tree component not available yet');
        // Try again after a short delay if component not available
        if (retryCount < maxRetries) {
          setTimeout(tryExpandTree, 500);
        } else {
          console.error('Failed to expand tree after maximum retry attempts');
        }
      }
    };

    // Start the retry process
    tryExpandTree();
  }

}
